import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { createUser, existeNick } from '../objects/user';
import { PERFILES as NIVELES_PERMISO } from '../utils/perfiles';


/**
 * Modal para dar de alta un nuevo usuario del sistema — visible solo para
 * el perfil "Mesa de Ayuda" (ver AgendaPage.js). Escribe directamente en la
 * colección de usuarios con el mismo SDK de cliente que usa toda la app.
 *
 * ⚠️ Nota de seguridad que vale la pena tener presente: el sistema de
 * autenticación de esta app (Login.js) compara la contraseña en texto
 * plano contra lo guardado en Firestore (`data.Pass === pass`) — no hay
 * hash ni cifrado. Este modal no cambia ese diseño (ya existía así desde
 * antes), pero al facilitar la creación de más usuarios, vale la pena
 * tenerlo en el radar como algo a resolver más adelante.
 */
const CreateUserModal = ({ onClose, onCreated, uhList }) => {
  const [nombre, setNombre] = useState('');
  const [nick, setNick] = useState('');
  const [pass, setPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [nivel, setNivel] = useState('agenda');
  const [unidadHospital, setUnidadHospital] = useState('');
  const [guardando, setGuardando] = useState(false);

  const handleGuardar = async () => {
    const nombreLimpio = nombre.trim();
    const nickLimpio = nick.trim().replace(/\s/g, '');

    if (!nombreLimpio || !nickLimpio || !pass || !unidadHospital) {
      Swal.fire({ icon: 'warning', title: 'Faltan datos', text: 'Completa nombre, usuario, contraseña y unidad hospitalaria.' });
      return;
    }
    if (pass.length < 4) {
      Swal.fire({ icon: 'warning', title: 'Contraseña muy corta', text: 'Usa al menos 4 caracteres.' });
      return;
    }

    setGuardando(true);

    try {
      const yaExiste = await existeNick(nickLimpio);
      if (yaExiste) {
        setGuardando(false);
        Swal.fire({ icon: 'error', title: 'Usuario ya existe', text: `Ya hay un usuario registrado con "${nickLimpio}". Elige otro nombre de usuario.` });
        return;
      }

      const uh = uhList.find((u) => u.hospital === unidadHospital);
      const unidadHospitalaria = { hospital: unidadHospital, calendar: uh?.calendar || '' };

      const res = await createUser(nivel, nombreLimpio, unidadHospitalaria, pass, nickLimpio);

      setGuardando(false);

      if (res === 'success') {
        Swal.fire({ icon: 'success', title: 'Usuario creado', timer: 1600, showConfirmButton: false });
        onCreated && onCreated();
        onClose();
      } else {
        Swal.fire({ icon: 'error', title: 'No se pudo crear el usuario', text: 'Intenta nuevamente.' });
      }
    } catch (err) {
      setGuardando(false);
      Swal.fire({ icon: 'error', title: 'No se pudo crear el usuario', text: 'Intenta nuevamente.' });
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(17,24,39,.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '440px',
          maxHeight: '88vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(0,0,0,.25)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ color: '#7c3aed', fontSize: '15px', fontWeight: 800 }}>👤 Nuevo usuario</span>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', color: '#374151', cursor: 'pointer', fontSize: '16px', height: '30px', width: '30px', lineHeight: 1 }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 22px', overflowY: 'auto' }}>

          <Label>Nombre completo</Label>
          <input
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Nombre y apellidos"
            style={inputStyle}
          />

          <div style={{ height: '14px' }} />
          <Label>Usuario</Label>
          <input
            type="text"
            value={nick}
            onChange={(e) => setNick(e.target.value.replace(/\s/g, ''))}
            placeholder="Nombre de usuario para iniciar sesión"
            style={inputStyle}
          />

          <div style={{ height: '14px' }} />
          <Label>Contraseña</Label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPass ? 'text' : 'password'}
              value={pass}
              onChange={(e) => setPass(e.target.value.replace(/\s/g, ''))}
              placeholder="Mínimo 4 caracteres"
              style={{ ...inputStyle, paddingRight: '38px' }}
            />
            <button
              type="button"
              onClick={() => setShowPass((v) => !v)}
              aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}
            >
              {showPass ? '🙈' : '👁'}
            </button>
          </div>

          <div style={{ height: '14px' }} />
          <Label>Unidad hospitalaria</Label>
          <select
            value={unidadHospital}
            onChange={(e) => setUnidadHospital(e.target.value)}
            style={inputStyle}
          >
            <option value="">Selecciona una unidad…</option>
            {(uhList || []).map((u) => (
              <option key={u.id} value={u.hospital}>{u.hospital}</option>
            ))}
          </select>

          <div style={{ height: '14px' }} />
          <Label>Nivel de permiso</Label>
          <select
            value={nivel}
            onChange={(e) => setNivel(e.target.value)}
            style={inputStyle}
          >
            {NIVELES_PERMISO.map((n) => (
              <option key={n.value} value={n.value}>{n.label}</option>
            ))}
          </select>
          <p style={{ color: '#7c3aed', fontSize: '11px', marginTop: '8px' }}>
            {nivel === 'agenda'     && 'ℹ️ Agenda: gestiona y programa cirugías de su unidad (reservar, confirmar, reagendar, cancelar y registrar realizado).'}
            {nivel === 'consulta'   && 'ℹ️ Consulta: solo lectura de su unidad. Puede ver todas las pestañas y descargar Excel/PDF.'}
            {nivel === 'mesa_ayuda' && 'ℹ️ Mesa de Ayuda: solo lectura de todas las unidades con un solo usuario y gestión de usuarios (alta, activar/inactivar).'}
            {nivel === 'monitor'    && 'ℹ️ Monitor: para pantallas o estaciones de trabajo. Solo ve la ventana Agenda del día de su unidad, se actualiza sola y no permite acciones ni descargas.'}
          </p>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', gap: '8px', padding: '14px 22px', borderTop: '1px solid #f3f4f6' }}>
          <button
            onClick={onClose}
            disabled={guardando}
            style={{ background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px', color: '#374151', cursor: 'pointer', fontSize: '12px', fontWeight: 700, padding: '9px 16px' }}
          >
            Cancelar
          </button>
          <button
            onClick={handleGuardar}
            disabled={guardando}
            style={{
              marginLeft: 'auto', background: '#7c3aed', border: 'none', borderRadius: '8px',
              color: '#fff', cursor: guardando ? 'default' : 'pointer', fontSize: '12px', fontWeight: 700,
              padding: '9px 18px', opacity: guardando ? .7 : 1,
            }}
          >
            {guardando ? 'Creando…' : 'Crear usuario'}
          </button>
        </div>
      </div>
    </div>
  );
};

const inputStyle = {
  border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px',
  padding: '8px 10px', width: '100%', boxSizing: 'border-box',
};

const Label = ({ children }) => (
  <span style={{ color: '#6b7280', display: 'block', fontSize: '9px', fontWeight: 700, letterSpacing: '.5px', marginBottom: '6px', textTransform: 'uppercase' }}>
    {children}
  </span>
);

export default CreateUserModal;